import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { IFileDto } from '../../../config/fileConfig'
import { DataPromise } from '../../../context/AxiosContext'
import { MainDto } from '../../../config/filePartyConfig'
import Navigation, { NavigationState } from '../../../components/Form/Navigation'
import { ListItemDto } from '../../../config/partyConfig'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import useEntity from '../../../hooks/useEntity'
import { Button, makeStyles, mergeClasses, shorthands, tokens, Tooltip } from '@fluentui/react-components'
import { AddRegular, bundleIcon, CheckmarkStarburstFilled, CheckmarkStarburstRegular, NewFilled, NewRegular } from '@fluentui/react-icons'
import * as UUID from 'uuid'
import { DocketProceedingDto, DocketProceedingForm, DocketProceedingType } from '../../../components/Form/Docket/DocketProceedingForm'
import { Spinner, SpinnerSize } from '@fluentui/react'
import { useTranslation } from 'react-i18next'

type Props = {
    file: DataPromise<IFileDto>
    parties: DataPromise<MainDto[]>
    docketProceedings: DataPromise<DocketProceedingDto[]>
    reset: () => void
}

export type DocketProceedingProps = Props & {
    selected?: DocketProceedingState
    setSelected: (state: DocketProceedingState) => void
    judicialInstitutions: DataPromise<ListItemDto[]>
}

export type DocketProceedingState = NavigationState & {
    type?: DocketProceedingType
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
    },
    containerColumn: {
        gridTemplateColumns: 'min-content 1fr',
    },
    containerRow: {},
    sidebar: {
        display: 'flex',
        minWidth: 'max-content',
        flexWrap: 'nowrap',
        ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    },
    sidebarColumn: {
        flexDirection: 'column',
    },
    sidebarRow: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    createButtonRow: {
        marginRight: tokens.spacingHorizontalL,
    },
    createButtonColumn: {
        marginLeft: tokens.spacingHorizontalM,
    },
})

const CheckmarkStarburst = bundleIcon(CheckmarkStarburstFilled, CheckmarkStarburstRegular)
const NewIcon = bundleIcon(NewFilled, NewRegular)

export function DocketProceedingOverview(props: Props): ReactElement {
    const styles = useStyles()
    const { isViewport, viewport } = useViewport()
    const { t: translate } = useTranslation()

    const [selected, setSelected] = useState<DocketProceedingState>()
    const useColumns = useMemo(() => isViewport('>=', Viewport.XXL), [viewport])
    const [judicialInstitutions] = useEntity<ListItemDto[]>(() => ({ url: '/registration/docket-proceeding/judicial-court' }), false)

    const [lazyItems, , resetLazyItems] = useEntity<DocketProceedingDto[]>(
        () => ({ url: `/file/${props.file.data.id}/docket-proceeding` }),
        props.docketProceedings?.loaded ? null : false,
    )

    const docketProceedingsPromise = useMemo(() => props.docketProceedings || lazyItems, [props.docketProceedings, lazyItems])
    const isDocketProceedingsLoaded = useMemo(() => docketProceedingsPromise.loaded, [props.docketProceedings, lazyItems])
    const docketProceedings = useMemo(
        () => (isDocketProceedingsLoaded ? docketProceedingsPromise.data?.sort((a, b) => a.proceedingNumber.localeCompare(b.proceedingNumber)) : []),
        [isDocketProceedingsLoaded],
    )

    if (!isDocketProceedingsLoaded) {
        return <Spinner label={translate('base.loading')} size={SpinnerSize.small} labelPosition="right" />
    }

    return (
        <div className={mergeClasses(styles.container, useColumns ? styles.containerColumn : styles.containerRow)}>
            <div className={mergeClasses(styles.sidebar, useColumns ? styles.sidebarColumn : styles.sidebarRow)}>
                <div>
                    <Tooltip relationship="description" content="Maak een nieuwe procedure aan" withArrow>
                        <Button
                            disabled={selected?.isNew}
                            appearance="primary"
                            icon={<AddRegular />}
                            className={mergeClasses(useColumns ? styles.createButtonColumn : styles.createButtonRow)}
                            onClick={() =>
                                setSelected({
                                    id: UUID.v4(),
                                    isNew: true,
                                    isDirty: false,
                                })
                            }
                        >
                            Nieuw
                        </Button>
                    </Tooltip>
                </div>
                <Navigation<DocketProceedingDto, DocketProceedingState>
                    entities={docketProceedings}
                    isLoaded={isDocketProceedingsLoaded}
                    getEntityKey={entity => entity.id}
                    getEntityId={entity => entity.id}
                    getEntityLabel={entity => entity.proceedingNumber}
                    getEntityIcon={entity => (entity.proceedingNumber ? <CheckmarkStarburst /> : <NewIcon />)}
                    selected={selected}
                    setSelected={setSelected}
                    createSelectedState={entity => ({
                        id: entity.id,
                        type: entity.type,
                        isNew: false,
                        isDirty: false,
                        judicialInstitutionId: undefined,
                    })}
                    useColumns={useColumns}
                />
            </div>
            <DocketProceedingForm
                {...props}
                docketProceedings={docketProceedingsPromise}
                reset={props.reset || resetLazyItems}
                selected={selected}
                setSelected={setSelected}
                judicialInstitutions={judicialInstitutions}
            />
        </div>
    )
}
